/**
 * The portfolio document. 06-transition section 5, the design's `PORT` model.
 *
 * The subgraph stores this as JSONB and this file is where its shape is
 * defined, so there is one description of it rather than one per component.
 */

export type PortfolioColour = 'navy' | 'green' | 'plum';
export type PortfolioLayout = 'one' | 'two';

/**
 * Hidden is not deleted. 06 section 5: taking a section off the page leaves it
 * in "Sections you can add" with whatever the member wrote still in it,
 * because a control with no way back is a trap.
 */
export type SectionState = 'hidden' | 'placed';

export interface PortfolioSection {
  state: SectionState;
  /** Whatever the section's own fields hold. The builder owns this shape. */
  content: Record<string, unknown>;
}

export interface PortfolioDocument {
  name: string;
  colour: PortfolioColour;
  layout: PortfolioLayout;
  available: string;
  /**
   * The role the page states. Follows the resume until the member overrides it:
   * a page headline and a resume filename are not the same decision.
   */
  role: string;
  roleFollowsResume: boolean;
  /** Section order, member-controlled. */
  order: string[];
  sections: Record<string, PortfolioSection>;
}

/** One field in a section's entry. */
export interface PortfolioField {
  key: 'title' | 'place' | 'year' | 'note';
  label: string;
  /** The quieter half of the label: "optional", or an example. */
  note?: string;
  /**
   * A field the member writes prose into, with the toolbar and Via. Only
   * projects and awards have one: everything else is a line, not a paragraph.
   */
  long?: boolean;
}

/**
 * How a section takes its content.
 *
 * `rich` is one prose field and nothing else. `resume` is read from the resume
 * rather than typed here, so there is one copy of it and it cannot drift.
 * `items` is a list of entries, each shaped by `fields`. `choice` is a set of
 * options, which is what "how to reach me" is.
 */
export type PortfolioSectionKind = 'rich' | 'resume' | 'items' | 'choice';

export interface PortfolioSectionMeta {
  key: string;
  name: string;
  /** The line under the section's name on its bar. */
  sub: string;
  /** Which glyph opens the section's fold bar, as the design gives each one. */
  icon: string;
  /** Optional sections are the ones a member adds; the rest start placed. */
  optional: boolean;
  kind: PortfolioSectionKind;
  /** For an `items` section: what one entry is shaped like. */
  fields?: PortfolioField[];
  /**
   * For an `items` section: what one entry is called, so the button can name
   * the thing being added rather than the act of writing it.
   */
  one?: string;
  /**
   * Where a section's content really comes from. T.7 removed the line that
   * rendered it ("Filled in from {{ section.source }}") because it was shown
   * unconditionally and two of these were false at the time -- Education and
   * Skills claimed a source while both were actually fed from the resume.
   * Kept here, unrendered, now that the claims are true: this is the record of
   * where each section's record offer actually reads from, not a UI string
   * waiting for a template to come back.
   */
  source?: string;
}

/**
 * The sections and their order out of the box. `FIELDS` / `SUBS` in the design.
 * Contact is last because it is what a visitor reads once they have decided to
 * get in touch.
 */
export const PORTFOLIO_SECTIONS: PortfolioSectionMeta[] = [
  {
    key: 'about',
    sub: 'Three or four lines a stranger reads first',
    kind: 'rich',
    name: 'About',
    icon: 'lines',
    optional: false
  },
  // Experience comes straight after About in the design's published page, and
  // was missing entirely: a page about someone leaving service with nowhere to
  // say what they did is the wrong shape.
  {
    key: 'experience',
    sub: 'Where you have worked, and what you did there',
    kind: 'resume',
    // The shape one job takes once it has been copied in from the resume. The
    // section is not typed here, but it is PUBLISHED from here, so the public
    // page needs the same field table every other section prints from. Without
    // it the section published empty and a visitor saw no work history at all.
    fields: [
      { key: 'title', label: 'Job title' },
      { key: 'place', label: 'Where you worked, and when' },
      { key: 'note', label: 'What you did', long: true }
    ],
    name: 'Experience',
    icon: 'briefcase',
    optional: false
  },
  // "Credentials and training", as the design labels it: the section carries
  // ACE credit recommendations as well as certificates, and "Credentials"
  // alone reads as certificates only.
  {
    key: 'creds',
    sub: 'Certificates, licenses and service schools',
    kind: 'items',
    one: 'a certification',
    source: 'your record',
    fields: [
      { key: 'title', label: 'What you earned' },
      { key: 'year', label: 'Year' },
      { key: 'note', label: 'Anything a reader should know', note: 'optional' }
    ],
    name: 'Credentials and training',
    icon: 'badge',
    optional: false
  },
  {
    key: 'edu',
    sub: 'Degrees, certificates and service schools',
    kind: 'items',
    one: 'another school',
    source: 'your Joint Services Transcript',
    fields: [
      { key: 'title', label: 'What you earned' },
      { key: 'place', label: 'Where', note: 'school, or the service school' },
      { key: 'year', label: 'Year finished' }
    ],
    name: 'Education',
    icon: 'cap',
    optional: false
  },
  {
    key: 'skills',
    sub: 'What you are good at, strongest first',
    kind: 'items',
    one: 'a skill',
    source: 'your skills survey and job code',
    fields: [
      { key: 'title', label: 'Skill', note: 'name it the way a listing would' }
    ],
    name: 'Skills',
    icon: 'check',
    optional: false
  },
  {
    key: 'projects',
    sub: 'Something you built or wrote, and what came of it',
    kind: 'items',
    one: 'a project',
    fields: [
      { key: 'title', label: 'What it is' },
      { key: 'note', label: 'What you did, and what came of it', long: true }
    ],
    name: 'Projects and work samples',
    icon: 'folder',
    optional: true
  },
  {
    key: 'volunteer',
    sub: 'Unpaid work, and what you did',
    kind: 'items',
    one: 'volunteer experience',
    fields: [
      { key: 'title', label: 'What you did' },
      { key: 'place', label: 'Who for' },
      { key: 'year', label: 'When', note: 'a year, or a range' }
    ],
    name: 'Volunteer experience',
    icon: 'heart',
    optional: true
  },
  {
    key: 'languages',
    sub: 'What you speak, and how well',
    kind: 'items',
    one: 'a language',
    fields: [
      { key: 'title', label: 'Language' },
      { key: 'note', label: 'How well', note: 'native, fluent, or conversational' }
    ],
    name: 'Languages',
    icon: 'globe',
    optional: true
  },
  {
    key: 'awards',
    sub: 'Commendations and anything you were recognized for',
    kind: 'items',
    one: 'an award',
    fields: [
      { key: 'title', label: 'What you were given' },
      { key: 'year', label: 'Year' },
      { key: 'note', label: 'What it was for', long: true }
    ],
    name: 'Awards and achievements',
    icon: 'badge',
    optional: true
  },
  {
    key: 'refs',
    sub: 'People who will vouch for you',
    kind: 'items',
    one: 'a reference',
    fields: [
      { key: 'title', label: 'Name' },
      { key: 'place', label: 'Their title and where' },
      { key: 'note', label: 'How they know you', note: 'optional' }
    ],
    name: 'References',
    icon: 'people',
    optional: true
  },
  // Contact is last because it is what a visitor reads once they have decided
  // to get in touch.
  {
    key: 'contact',
    sub: 'How a visitor gets in touch with you',
    kind: 'choice',
    name: 'How to reach me',
    icon: 'mail',
    // Placed, not hidden: a page with no way to get in touch is the one thing
    // a portfolio cannot be missing, whatever else the member leaves out.
    optional: false
  }
];

/**
 * The masthead colours. Each carries what choosing it says about the page,
 * because "navy" on its own is a taste question and this is not one: the
 * member is deciding what a stranger reads the page as.
 */
export const PORTFOLIO_COLOURS: {
  value: PortfolioColour;
  label: string;
}[] = [
  {
    value: 'navy',
    // T-7: LABEL only. The stored value stays `navy`, so every
    // already-published portfolio keeps its colour.
    label: 'Dark Blue'
  },
  { value: 'green', label: 'Green' },
  { value: 'plum', label: 'Plum' }
];

/** How the body runs. Two arrangements. */
export const PORTFOLIO_LAYOUTS: {
  value: PortfolioLayout;
  label: string;
}[] = [
  { value: 'one', label: 'One column' },
  { value: 'two', label: 'Two columns' }
];

export function emptyPortfolio(): PortfolioDocument {
  return {
    name: '',
    colour: 'navy',
    layout: 'one',
    available: '',
    role: '',
    roleFollowsResume: true,
    order: PORTFOLIO_SECTIONS.filter((s) => !s.optional).map((s) => s.key),
    sections: PORTFOLIO_SECTIONS.reduce<Record<string, PortfolioSection>>(
      (acc, section) => {
        acc[section.key] = {
          state: section.optional ? 'hidden' : 'placed',
          content: {}
        };
        return acc;
      },
      {}
    )
  };
}

/**
 * Whether one value the member could have written holds anything.
 *
 * Recursive, because a section's entries are objects of fields and a list of
 * them is a list of those. An entry with every field blank is the form's own
 * empty state, not something written: "Add another" pushes exactly that, and
 * counting it made pressing the button once satisfy the publish gate on a
 * section with nothing in it.
 */
function holdsSomething(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(
      holdsSomething
    );
  }

  return true;
}

/**
 * Whether a section holds anything the member typed.
 *
 * Used by the publish gate, so it has to mean "filled" rather than "present":
 * an empty string, a whitespace-only string and a list of blank entries are
 * all nothing.
 */
export function isSectionFilled(
  section: PortfolioSection | undefined
): boolean {
  if (!section || section.state !== 'placed') {
    return false;
  }

  return Object.values(section.content ?? {}).some(holdsSomething);
}

/**
 * 06 acceptance criterion 6: Publish is disabled until there is a name and at
 * least one filled section, and the disabled state says why.
 *
 * Returns the reason rather than a boolean, so the button can explain itself
 * instead of being inert. Null means it is ready.
 */
export function publishBlockedReason(
  document: PortfolioDocument | null
): string | null {
  if (!document) {
    return 'Your page has not been started yet.';
  }

  const hasName = (document.name ?? '').trim().length > 0;
  const hasFilledSection = Object.values(document.sections ?? {}).some(
    isSectionFilled
  );

  if (!hasName && !hasFilledSection) {
    return 'Add your name and fill in at least one section before publishing.';
  }

  if (!hasName) {
    return 'Add the name your page should carry before publishing.';
  }

  return hasFilledSection
    ? null
    : 'Fill in at least one section before publishing.';
}
